import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export const PaymentsPage = () => {
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState({
    organisationName: "",
    amount: "",
    invoiceStartDate: "",
    invoiceEndDate: "",
    status: "Pending",
  });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [AllPayments, setAllPayments] = useState([]);
  const [organisations, setOrganizations] = useState([]);

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    // Implement API call to create a new invoice using paymentDetails state
    // After successful creation, fetch the updated list of payments and update AllPayments state
    const data ={
        organisation_name: paymentDetails.organisationName,
        amount: paymentDetails.amount,
        invoice_start_date: paymentDetails.invoiceStartDate,
        invoice_end_date: paymentDetails.invoiceEndDate,
        status: paymentDetails.status,
    }
    await fetch(`${API}/master/payments`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        // Assuming the API returns the updated list of payments after creation
        setAllPayments(prev => [...prev, data]);
        setShowAddDialog(false);
      })
      .catch((err) => console.error(err));
  };
  const fetchAllPayments = async () => {
    // Implement API call to fetch all payments and update AllPayments state
    await fetch(`${API}/master/payments`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => setAllPayments(data))
      .catch((err) => console.error(err));
  };

  const fetchOrganizations = async () => {
    // Implement API call to fetch all organizations and update organisations state
    await fetch(`${API}/master/organizations`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => setOrganizations(data))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    fetchAllPayments();
    fetchOrganizations();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="pt-12 pb-12 px-6 max-w-7xl mx-auto">
        <Button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/master/dashboard")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </Button>
        <div className="mb-12 fade-in">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
                All Payments
              </h1>
              <p className="text-xl text-gray-600">
                View and manage all client payments using ParkSmart.
              </p>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-vehicle-btn"
                  className="bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-full px-6 py-6"
                >
                  <Plus className="mr-2" size={20} />
                  New Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-[#1A3C34]">
                    Create New Invoice
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateInvoice} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium">
                      Organisation Name
                    </Label>
                    <Select
                      id="name"
                      data-testid="client-name"
                      placeholder="ABC Organization"
                      value={paymentDetails.organisationName}
                      onValueChange={(e) =>
                        setPaymentDetails({
                          ...paymentDetails,
                          organisationName: e,
                        })
                      }
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    >
                      <SelectTrigger
                        data-testid="select-organisation"
                        className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-14 text-base"
                      >
                        <SelectValue placeholder="Choose your organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Map through your organizations and create SelectItem for each */}
                        {organisations.map((org,index) => (
                          <SelectItem key={`org-${index}`} value={org}>
                            {org}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label
                      htmlFor="invoiceAmount"
                      className="text-gray-700 font-medium"
                    >
                      Invoice Amount
                    </Label>
                    <Input
                      id="invoiceAmount"
                      data-testid="client-invoice-amount"
                      placeholder="Enter invoice amount"
                      value={paymentDetails.amount}
                      onChange={(e) =>
                        setPaymentDetails({
                          ...paymentDetails,
                          amount: e.target.value,
                        })
                      }
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="invoiceStartDate"
                      className="text-gray-700 font-medium"
                    >
                      Invoice Start Date
                    </Label>
                    <Input
                    id="invoiceStartDate"
                    data-testid="select-date"
                    type="date"
                    value={paymentDetails.invoiceStartDate}
                    onChange={(e) =>
                      setPaymentDetails({
                        ...paymentDetails,
                        invoiceStartDate: e.target.value,
                      })
                    }
                    className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-14 text-base"
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                  </div>
                  <div>
                    <Label
                      htmlFor="invoiceEndDate"
                      className="text-gray-700 font-medium"
                    >
                      Invoice End Date
                    </Label>
                    <Input
                    id="invoiceEndDate"
                    data-testid="select-date"
                    type="date"
                    value={paymentDetails.invoiceEndDate}
                    onChange={(e) =>
                      setPaymentDetails({
                        ...paymentDetails,
                        invoiceEndDate: e.target.value,
                      })
                    }
                    className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-14 text-base"
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                  </div>
                  <Button
                    data-testid="submit-vehicle"
                    type="submit"
                    className="w-full bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full py-6"
                  >
                    Create Invoice
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="bg-white p-6 rounded-lg shadow">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left">Organisation Name</TableHead>
                  <TableHead className="text-left">Invoice Amount</TableHead>
                  <TableHead className="text-left">
                    Invoice Start Date
                  </TableHead>
                  <TableHead className="text-left">Invoice End Date</TableHead>
                  <TableHead className="text-left">Status</TableHead>
                </TableRow>
              </TableHeader>
              {/* Map through your payment details and create TableRow for each */}
              <TableBody>
                {AllPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell><Link to={`/master/paymentsDetails/${payment._id}`}>{payment.organisation_name}</Link></TableCell>
                    <TableCell>{payment.amount}</TableCell>
                    <TableCell>{payment.invoice_start_date}</TableCell>
                    <TableCell>{payment.invoice_end_date}</TableCell>
                    <TableCell>{payment.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};
